/**
 * File type identification utilities.
 *
 * Used to decide whether a tab should be treated as a Magic MD document
 * (with parser, magic-syntax CodeMirror extensions, DocumentRenderer preview)
 * or as a generic text/code file (with language-specific CodeMirror extension
 * and a type-specific preview).
 */

import { isCompressedExternalJsonPath } from "./external-output-path";

export type FileKind =
    | "markdown"
    | "json"
    | "html"
    | "image"
    | "svg"
    | "pdf"
    | "audio"
    | "video"
    | "csv"
    | "code"
    | "plain";

const MARKDOWN_EXTENSIONS = new Set(["md", "mdx", "markdown"]);
const JSON_EXTENSIONS = new Set(["json", "jsonc", "json5"]);
const HTML_EXTENSIONS = new Set(["html", "htm", "xhtml"]);
const IMAGE_EXTENSIONS = new Set([
    "png", "jpg", "jpeg", "gif", "webp", "bmp", "ico", "avif", "tiff", "tif",
]);
const SVG_EXTENSIONS = new Set(["svg"]);
const PDF_EXTENSIONS = new Set(["pdf"]);
const AUDIO_EXTENSIONS = new Set([
    "mp3", "wav", "ogg", "oga", "m4a", "flac", "aac", "opus",
]);
const VIDEO_EXTENSIONS = new Set([
    "mp4", "webm", "mov", "mkv", "avi", "m4v",
]);
const CSV_EXTENSIONS = new Set(["csv", "tsv"]);

const CODE_EXTENSIONS = new Set([
    // Web
    "ts", "tsx", "js", "jsx", "mjs", "cjs",
    "css", "scss", "sass", "less",
    "vue", "svelte",
    // Backend
    "py", "rb", "go", "rs", "java", "kt", "scala", "swift",
    "c", "cc", "cpp", "h", "hpp", "cs", "php", "lua",
    // Shell / config
    "sh", "bash", "zsh", "fish",
    "yaml", "yml", "toml", "ini", "env",
    "xml",
    // Data / query
    "sql", "graphql", "gql",
    // Other
    "dockerfile", "makefile",
]);

/** Kinds whose contents are not text — must be served via getAssetUrl. */
const BINARY_KINDS = new Set<FileKind>(["image", "pdf", "audio", "video"]);

export function isBinaryKind(kind: FileKind): boolean {
    return BINARY_KINDS.has(kind);
}

export function isBinaryFile(path?: string): boolean {
    if (!path) return false;
    return isBinaryKind(getFileKind(path));
}

/**
 * Returns the lowercase extension (without leading dot), or "" if none.
 * Also recognises common no-extension filenames (Dockerfile, Makefile).
 */
export function getFileExtension(path?: string): string {
    if (!path) return "";
    if (isCompressedExternalJsonPath(path)) return "json";
    const base = path.split(/[/\\]/).pop() ?? "";
    const lower = base.toLowerCase();
    if (lower === "dockerfile") return "dockerfile";
    if (lower === "makefile") return "makefile";
    const m = lower.match(/\.([^.]+)$/);
    return m ? m[1] : "";
}

/**
 * True for Markdown-family files. Untitled tabs (no filePath) are treated as
 * markdown to preserve the "new document" UX.
 */
export function isMarkdownFile(path?: string): boolean {
    if (!path) return true;
    return MARKDOWN_EXTENSIONS.has(getFileExtension(path));
}

export function getFileKind(path?: string): FileKind {
    const ext = getFileExtension(path);
    if (!path) return "markdown";
    if (MARKDOWN_EXTENSIONS.has(ext)) return "markdown";
    if (JSON_EXTENSIONS.has(ext)) return "json";
    if (HTML_EXTENSIONS.has(ext)) return "html";
    if (SVG_EXTENSIONS.has(ext)) return "svg";
    if (IMAGE_EXTENSIONS.has(ext)) return "image";
    if (PDF_EXTENSIONS.has(ext)) return "pdf";
    if (AUDIO_EXTENSIONS.has(ext)) return "audio";
    if (VIDEO_EXTENSIONS.has(ext)) return "video";
    if (CSV_EXTENSIONS.has(ext)) return "csv";
    if (CODE_EXTENSIONS.has(ext)) return "code";
    return "plain";
}
